import Colors from '~/client/src/shared/theme.module.scss'

// The plate's SHAPE, as data: no canvas, no component, so the layers that place a plate and the
// rasterizer that paints one read the same source and can be tested without either. One 9-patch
// for both tiers, or they drift. Its corners are fixed 9-patch material exactly as the chip is,
// so the same clamp applies — see PLATE_PERSPECTIVE_FLOOR.

export interface ILabelPlateSpec {
  radiusPx: number
  padXPx: number
  padYPx: number
  // 0 for a plate with no chip. A chip is FIXED material, so it sets the clamp floor: see
  // labelPlateFit.
  chipPx: number
  chipGapPx: number
  fill: string
}

// The spec's plate, with the one number it could not settle: a chip inside is FIXED 9-patch
// material and sets the clamp floor, so the padding is derived from the chip rather than stated
// — see minPlatePadYPx. The chip stays the map's own 20px; shrinking it to balance the spec's
// arithmetic would make the chip's size depend on whether its label stands on a plan sheet.
export const WORKFLOW_LABEL_PLATE: ILabelPlateSpec = {
  radiusPx: 3,
  padXPx: 4,
  padYPx: 3,
  chipPx: 20,
  chipGapPx: 4,
  // The casing's own token, given a shape rather than a stroke. Not a new one: the plate IS
  // the casing on a plated label, which is why the halo goes to zero beside it — so a change
  // to one has to reach the other, and a second token would let them drift.
  fill: Colors.mapHaloOnLight,
}

// The same plate with nothing inside it, for a label that carries no company logo and for the
// location tier, which never had a chip. Its horizontal padding comes from the layer as a zoom
// expression, so the only fixed material left is the four corners.
export const PLAIN_LABEL_PLATE: ILabelPlateSpec = {
  ...WORKFLOW_LABEL_PLATE,
  chipPx: 0,
}

// The selection ring: 2px of primary over 1px of the map's light coat, and the coat sits
// OUTSIDE the ring only. Painting it on both sides put white inside the box as well, which read
// as a glow rather than as the thin coat every other mark on this map wears.
export const SELECTION_RING = {
  widthPx: 2,
  casingPx: 1,
} as const

// The ring's clearance from the item's painted edge. It sits PROUD rather than hugging: an
// outline flush against the plate would read as the label having gained a border, which is the
// editor restyling the thing it is supposed to be previewing.
export const SELECTION_RING_MARGIN_PX = 4

// How far the ring's own centreline sits in from the image's edge — the casing outside it, plus
// half its own width.
const RING_INSET_PX = SELECTION_RING.casingPx + SELECTION_RING.widthPx / 2

// What a selected label wears when it had no plate: the same 9-patch, no fill — selecting must
// not give a bare label a plate. Its radius is the plate's PLUS the ring's inset, because
// insetting a rounded rect shrinks its curve by exactly the inset.
export const OUTLINE_ONLY_PLATE: ILabelPlateSpec = {
  ...PLAIN_LABEL_PLATE,
  fill: 'rgba(0, 0, 0, 0)',
  radiusPx: PLAIN_LABEL_PLATE.radiusPx + RING_INSET_PX,
}

// A symbol's perspective ratio has a theoretical floor of 0.5, so fitting into twice the fixed
// height puts the clamp mathematically out of reach. The chip image takes that deal — its clamp
// padding is invisible transparent band and costs nothing to over-buy.
//
// The plate cannot: it PAINTS its whole fitted box, so at 2x a one-line label reads as a slab.
// It targets a MEASURED worst case instead, below the ratio observed at extreme pitch. The clamp
// then binds only past roughly pitch 60 at close range, and binds gently — the plate rides a few
// percent large rather than freezing outright.
const PERSPECTIVE_FLOOR = 0.5
// Measured rather than theoretical — see above. Plate only.
//
// 0.80 rather than the 0.90 measured, and the margin is load-bearing: the clamp binds on
// icon-size TIMES the perspective ratio, not the ratio alone, and icon-size by itself dips
// under 0.90 around zoom 11 — so 0.90 binds there even at pitch 0.
const PLATE_PERSPECTIVE_FLOOR = 0.8

export interface ILabelPlateFit {
  // Fixed column left of the text: chip plus its gap, plus the padding outside it.
  leadPx: number
  // Mapbox's fixedContentHeight — the non-stretching height inside the content box.
  fixedContentHeightPx: number
  // The smallest fitted box that keeps the clamp out of reach at a measured worst-case
  // perspective ratio.
  minFitHeightPx: number
  // The smallest that keeps it out of reach at the ratio's theoretical floor.
  strictFitHeightPx: number
}

export function labelPlateFit(spec: ILabelPlateSpec): ILabelPlateFit {
  // With a chip, the chip is the fixed row and nothing else can stretch through it. Without
  // one, only the two corner rows are fixed.
  const fixedContentHeightPx = spec.chipPx || spec.radiusPx * 2
  return {
    // With no chip the left edge holds nothing but the corner, so the lead IS the radius.
    // Baking padXPx there instead would put 4 fixed pixels on the left against 3 on the
    // right, and a baked 4px swamps a 1.8px label at the bottom of the location ramp —
    // padding belongs on the layer as a zoom expression.
    leadPx: spec.chipPx
      ? spec.padXPx + spec.chipPx + spec.chipGapPx
      : spec.radiusPx,
    fixedContentHeightPx,
    minFitHeightPx: fixedContentHeightPx / PLATE_PERSPECTIVE_FLOOR,
    // What the same material would need to be bulletproof, which is what the CHIP image asks
    // for. Kept so the two floors stay visibly related rather than one being a magic number.
    strictFitHeightPx: fixedContentHeightPx / PERSPECTIVE_FLOOR,
  }
}

// The vertical padding a block of text needs before its fitted box clears the clamp. Takes the
// BLOCK height, not one line: a label already taller than the fit floor needs none, and the
// plate paints its whole fitted box, so a worst-case constant would make every label wear the
// shortest one's padding.
export function minPlatePadYPx(
  spec: ILabelPlateSpec,
  textHeightPx: number,
): number {
  const { minFitHeightPx } = labelPlateFit(spec)
  // Floors at zero, not at padYPx: the plate's corner rows sit outside the fitted box and are
  // emitted at fixed size, so they already hold the text off the painted edge by the radius.
  // Adding padYPx on top would double it, and a two- or three-line plate would miss the spec
  // by exactly that.
  return Math.max(0, (minFitHeightPx - textHeightPx) / 2)
}
